import { useState, useEffect } from "react";
import { NavLink, useNavigate, Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Logo } from "@/components/Logo";
import {
  Gauge, Car, CreditCard, Buildings, ChatCircleText, Briefcase, Star,
  MapPinLine, Megaphone, Gear, SignOut, List, X, Bell, Images, CalendarCheck, HandHeart, ClipboardText,
} from "@phosphor-icons/react";

const links = [
  { to: "/admin", label: "Overview", icon: Gauge, end: true },
  { to: "/admin/follow-ups", label: "Follow-Ups Due", icon: CalendarCheck, followupBadge: true },
  { to: "/admin/ride-requests", label: "Ride Requests", icon: Car, statKey: "ride-requests" },
  { to: "/admin/quotes", label: "Quote Requests", icon: CreditCard, statKey: "quotes" },
  { to: "/admin/facility-leads", label: "Facility Leads", icon: Buildings, statKey: "facility-leads" },
  { to: "/admin/odp-referrals", label: "ODP Referrals", icon: HandHeart, statKey: "odp-referrals" },
  { to: "/admin/contacts", label: "Contact Messages", icon: ChatCircleText, statKey: "contacts" },
  { to: "/admin/applications", label: "Job Applications", icon: Briefcase, statKey: "applications" },
  { to: "/admin/job-openings", label: "Job Openings", icon: ClipboardText },
  { to: "/admin/testimonials", label: "Testimonials", icon: Star },
  { to: "/admin/service-areas", label: "Service Areas", icon: MapPinLine },
  { to: "/admin/media", label: "Media Library", icon: Images },
  { to: "/admin/announcements", label: "Announcements", icon: Megaphone },
  { to: "/admin/content", label: "Site Content", icon: Gear },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState({});

  useEffect(() => {
    if (user) api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [user, location.pathname]);

  if (user === null) return <div className="flex min-h-screen items-center justify-center text-slate-400">Loading…</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;

  const doLogout = async () => { await logout(); navigate("/admin/login"); };
  const totalNew = ["ride-requests", "quotes", "facility-leads", "odp-referrals", "contacts", "applications"]
    .reduce((sum, k) => sum + (stats[k]?.new || 0), 0);

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 transform bg-brand-blue transition-transform lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex h-16 items-center justify-between px-5">
          <Logo variant="light" />
          <button className="lg:hidden text-white" onClick={() => setOpen(false)}><X size={22} /></button>
        </div>
        <nav className="mt-4 space-y-1 px-3">
          {links.map((l) => {
            const newCount = l.statKey ? (stats[l.statKey]?.new || 0) : 0;
            const fuCount = l.followupBadge ? ((stats.follow_ups?.overdue || 0) + (stats.follow_ups?.due_today || 0)) : 0;
            const badge = newCount || fuCount;
            return (
              <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)}
                data-testid={`admin-nav-${l.to.split("/").pop() || "overview"}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive ? "bg-white/10 text-white" : "text-slate-300 hover:bg-white/5 hover:text-white"
                  }`}>
                <l.icon size={20} weight="duotone" />
                <span className="flex-1">{l.label}</span>
                {badge > 0 && (
                  <span data-testid={`nav-badge-${l.statKey || 'followups'}`} className={`rounded-full px-2 py-0.5 text-[11px] font-bold text-white ${fuCount ? "bg-rose-500" : "bg-brand-purple"}`}>{badge}</span>
                )}
              </NavLink>
            );
          })}
        </nav>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-4 sm:px-6">
          <button data-testid="admin-hamburger" className="lg:hidden text-brand-blue" onClick={() => setOpen(true)}><List size={24} /></button>
          <div className="flex-1" />
          <div className="flex items-center gap-4">
            <span data-testid="header-new-count" className="relative flex items-center gap-1.5 rounded-full bg-brand-purple/10 px-3 py-1.5 text-sm font-semibold text-brand-purple">
              <Bell size={16} weight="fill" /> {totalNew} new
            </span>
            <span className="hidden text-sm text-slate-600 sm:block">{user.email}</span>
            <button onClick={doLogout} data-testid="admin-logout"
              className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              <SignOut size={18} /> Logout
            </button>
          </div>
        </header>
        <main className="p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
